// The icon rail (docs/UI-POLISH.md decision 4): the mark, the five areas of the game on screen, the account at the bottom.
import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { ExternalLink, FileJson, KeyRound, Lock, LogOut, Monitor, Moon, Sun, User, type LucideIcon } from '../ui/icons';
import { Switch } from '../ui/inputs';
import { Popover } from '../ui/Popover';
import { loadDensity, loadTheme, saveDensity, saveTheme, type ThemeChoice } from '../ui/theme';
import { Mark } from './Brand';
import { routes } from './routes';

export interface RailArea {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
  active: boolean;
}

export function Rail({ areas = [] }: { areas?: RailArea[] }) {
  return (
    <nav className="rail" aria-label="Areas">
      <Link to={routes.orgs()} className="rail-brand" aria-label="Huelency: organizations" title="Huelency">
        <Mark size={26} />
      </Link>
      {areas.map((a) => (
        <Link key={a.key} to={a.to} className={`rail-item ${a.active ? 'active' : ''}`} aria-current={a.active ? 'page' : undefined}>
          <a.icon aria-hidden="true" />
          <span>{a.label}</span>
        </Link>
      ))}
      <span className="rail-spacer" />
      <AccountButton />
    </nav>
  );
}

const themeChoices: { key: ThemeChoice; label: string; icon: LucideIcon }[] = [
  { key: 'light', label: 'Light', icon: Sun },
  { key: 'dark', label: 'Dark', icon: Moon },
  { key: 'system', label: 'System', icon: Monitor },
];

/** The account button and its menu: the pages of the account, the theme, the density, sign out. */
function AccountButton() {
  const { session, signOut } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [theme, setTheme] = useState<ThemeChoice>(() => loadTheme());
  const [compact, setCompact] = useState(() => loadDensity() === 'compact');
  const email = session?.email ?? '';
  const initials = email.replace(/@.*/, '').slice(0, 2).toUpperCase() || '·';
  const close = () => setAnchor(null);
  return (
    <>
      <button type="button" className="rail-avatar" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" aria-expanded={anchor !== null} aria-label="Account menu" title={email}>
        {initials}
      </button>
      <Popover anchor={anchor} open={anchor !== null} onClose={close} label="Account" placement="right-end" className="account-menu">
        <div className="account-head">
          <span className="rail-avatar" aria-hidden="true">
            {initials}
          </span>
          <span className="account-email" title={email}>
            {email}
          </span>
        </div>
        <Link className="menu-item" to={routes.account()} onClick={close}>
          <User aria-hidden="true" />
          Profile
        </Link>
        <Link className="menu-item" to={routes.tokens()} onClick={close}>
          <KeyRound aria-hidden="true" />
          Personal tokens
        </Link>
        <Link className="menu-item" to={routes.password()} onClick={close}>
          <Lock aria-hidden="true" />
          Change password
        </Link>
        <a className="menu-item" href="/openapi/v1.json" target="_blank" rel="noreferrer">
          <FileJson aria-hidden="true" />
          OpenAPI document
          <ExternalLink className="menu-trail" aria-hidden="true" />
        </a>
        <div className="menu-sep" role="separator" />
        <div className="account-pref">
          <span>Theme</span>
          <div className="segmented" role="radiogroup" aria-label="Theme">
            {themeChoices.map((c) => (
              <button
                key={c.key}
                type="button"
                role="radio"
                aria-checked={theme === c.key}
                className={`tab ${theme === c.key ? 'active' : ''}`}
                title={c.label}
                onClick={() => {
                  saveTheme(c.key);
                  setTheme(c.key);
                }}
              >
                <c.icon aria-hidden="true" />
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="account-pref">
          <Switch
            label="Compact density"
            checked={compact}
            onChange={(v) => {
              saveDensity(v ? 'compact' : 'comfortable');
              setCompact(v);
            }}
          />
        </div>
        <div className="menu-sep" role="separator" />
        <button type="button" className="menu-item" onClick={() => void signOut()}>
          <LogOut aria-hidden="true" />
          Sign out
        </button>
      </Popover>
    </>
  );
}
